/**
 * Roadmap API Service
 * 
 * Provides access to Case Strengthening Roadmap endpoints.
 * 
 * IMPORTANT: Client-safe endpoints return NO financial data.
 * Staff endpoints include all financial details.
 */

import api from '../utils/api';

// =============================================================================
// TYPES
// =============================================================================

export interface ActionSummary {
  id: string;
  priority: number;
  action: string;
  action_type: string;
  status: string;
  deadline_days: number;
  deadline_date?: string;
  difficulty: string;
  estimated_minutes: number;
}

export interface RoadmapSummary {
  id: string;
  report_id: string;
  version: number;
  generated_at: string;
  is_current: boolean;
  case_strength: number;
  target_strength: number;
  ready_for_demand: boolean;
  total_actions: number;
  completed_actions: number;
  percent_complete: number;
  top_actions: ActionSummary[];
}

export interface RoadmapAction {
  id: string;
  priority: number;
  action: string;
  action_type: string;
  addresses: string[];
  source_agents: string[];
  
  // STAFF ONLY - Financial data
  impact_strength_points: number;
  impact_description: string;
  value_unlock: number;
  risk_mitigated: number;
  calculation_method: string;
  calculation_id: string;
  is_estimate: boolean;
  
  // Execution
  deadline_days: number;
  deadline_date?: string;
  assigned_to: string;
  
  // Effort
  estimated_minutes: number;
  difficulty: string;
  difficulty_reason: string;
  how_to_complete: string;
  
  // Dependencies
  depends_on: string[];
  unlocks: string[];
  dependency_level?: number;
  
  // Document matching
  document_category?: string;
  document_specifics?: string;
  matching_keywords: string[];
  
  // Status
  status: string;
  completed_at?: string;
  completed_by_document_id?: string;
  notes: string;
}

export interface StateSnapshot {
  case_strength: number;
  ready_for_demand: boolean;
  estimated_value: number;
  data_quality_grade: string;
  blocking_issues_count: number;
  vulnerabilities_count: number;
  unclaimed_damages: number;
}

export interface TargetSnapshot {
  achievable_strength: number;
  estimated_value_increase: number;
  new_estimated_value: number;
  days_to_ready: number;
  confidence: number;
}

export interface ProgressMetrics {
  total_actions: number;
  completed_actions: number;
  in_progress_actions: number;
  blocked_actions: number;
  original_strength: number;
  current_strength: number;
  target_strength: number;
  value_unlocked_so_far: number;
  value_remaining: number;
  percent_complete: number;
  estimated_days_remaining: number;
}

export interface RoadmapFull {
  id: string;
  report_id: string;
  version: number;
  generated_at: string;
  is_current: boolean;
  current_state: StateSnapshot;
  target_state: TargetSnapshot;
  progress: ProgressMetrics;
  actions: RoadmapAction[];
  has_dependencies: boolean;
  dependency_levels: number;
  figures_currency?: {
    superseded?: boolean;
    current_run_id?: string | null;
    current_run_at?: string | null;
    analysis_at?: string | null;
  } | null;
}

export interface ClientAction {
  id: string;
  priority: number;
  action: string;
  action_type: string;
  status: string;
  deadline_days: number;
  deadline_date?: string;
  difficulty: string;
  estimated_minutes: number;
  how_to_complete: string;
  assigned_to: string;
}

export interface RoadmapClientView {
  id: string;
  report_id: string;
  version: number;
  generated_at: string;
  total_actions: number;
  completed_actions: number;
  percent_complete: number;
  estimated_days_remaining: number;
  actions: ClientAction[];
}

export interface RoadmapHistoryItem {
  id: string;
  version: number;
  generated_at: string;
  is_current: boolean;
  total_actions: number;
  completed_actions: number;
  percent_complete: number;
}

export interface GeneratedEmail {
  subject: string;
  body: string;
  document_type: string;
  deadline_date: string;
  deadline_days: number;
  estimated_time: string;
  difficulty: string;
}

export interface StaffEmailView {
  client_email: GeneratedEmail;
  value_unlock: number;
  risk_mitigated: number;
  impact_description: string;
  priority: number;
  staff_notes: string;
  why_important: string;
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

export const roadmapApi = {
  /**
   * Get roadmap summary
   */
  async getSummary(reportId: string): Promise<RoadmapSummary> {
    return await api.get(`/api/v1/roadmap/${reportId}`);
  },

  /**
   * Get full roadmap (STAFF ONLY - includes financial data)
   * Returns null if no roadmap exists (suppresses error toast)
   */
  async getFullRoadmap(reportId: string): Promise<RoadmapFull | null> {
    try {
      return await api.get(`/api/v1/roadmap/${reportId}/full`, { showErrorToast: false });
    } catch {
      return null;
    }
  },

  /**
   * Get client-safe roadmap view (NO financial data)
   */
  async getClientView(reportId: string): Promise<RoadmapClientView> {
    return await api.get(`/api/v1/roadmap/${reportId}/client-view`);
  },

  /**
   * Generate or refresh roadmap
   */
  async generateRoadmap(reportId: string, forceRefresh: boolean = false): Promise<any> {
    return await api.post(`/api/v1/roadmap/${reportId}/generate`, {
      force_refresh: forceRefresh
    });
  },

  /**
   * Update action status
   */
  async updateActionStatus(
    reportId: string,
    actionId: string,
    status: string,
    notes?: string,
    documentId?: string
  ): Promise<any> {
    return await api.patch(`/api/v1/roadmap/${reportId}/action/${actionId}`, {
      status,
      notes,
      completed_by_document_id: documentId
    });
  },

  /**
   * Get roadmap version history
   */
  async getHistory(reportId: string): Promise<RoadmapHistoryItem[]> {
    return await api.get(`/api/v1/roadmap/${reportId}/history`);
  },

  /**
   * Get pending actions
   */
  async getPendingActions(reportId: string): Promise<any> {
    return await api.get(`/api/v1/roadmap/${reportId}/pending-actions`);
  },

  /**
   * Generate client email for an action (NO financial data)
   */
  async generateClientEmail(
    reportId: string,
    actionId: string,
    clientName: string,
    clientEmail: string,
    uploadLink: string,
    options?: {
      senderName?: string;
      companyName?: string;
      incidentDate?: string;
      dateRange?: string;
    }
  ): Promise<GeneratedEmail> {
    return await api.post(`/api/v1/roadmap/${reportId}/action/${actionId}/generate-email`, {
      action_id: actionId,
      client_name: clientName,
      client_email: clientEmail,
      upload_link: uploadLink,
      sender_name: options?.senderName || 'The Figure Co. Support',
      company_name: options?.companyName || 'The Figure Co.',
      incident_date: options?.incidentDate,
      date_range: options?.dateRange
    });
  },

  /**
   * Generate staff email view (WITH financial data)
   */
  async generateStaffEmailView(
    reportId: string,
    actionId: string,
    clientName: string,
    clientEmail: string,
    uploadLink: string,
    options?: {
      senderName?: string;
      companyName?: string;
      incidentDate?: string;
      dateRange?: string;
    }
  ): Promise<StaffEmailView> {
    return await api.post(`/api/v1/roadmap/${reportId}/action/${actionId}/generate-email/staff`, {
      action_id: actionId,
      client_name: clientName,
      client_email: clientEmail,
      upload_link: uploadLink,
      sender_name: options?.senderName || 'The Figure Co. Support',
      company_name: options?.companyName || 'The Figure Co.',
      incident_date: options?.incidentDate,
      date_range: options?.dateRange
    });
  },

  /**
   * Match document to action manually
   */
  async matchDocumentToAction(
    reportId: string,
    actionId: string,
    documentId: string,
    documentFilename: string
  ): Promise<any> {
    // Inline query params into the URL — the modern api wrapper
    // doesn't support axios-style `{ params: {...} }`. Pre-2026-04-26
    // this code passed params through an axios option that the
    // wrapper silently ignored, meaning document_id / document_filename
    // were never sent to the backend (real lost-feature fossil).
    // 2026-04-26 archaeology pass.
    const qs = new URLSearchParams({
      document_id: documentId,
      document_filename: documentFilename,
    }).toString();
    return await api.post(
      `/api/v1/roadmap/${reportId}/action/${actionId}/match-document?${qs}`,
      null
    );
  }
};

export default roadmapApi;
